import { Suspense } from 'react';
import {
  Navigate,
  Outlet,
  RouteObject,
  createBrowserRouter,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router';
import App from 'App';
import AuthLayout from 'layouts/auth-layout';
import DefaultAuthLayout from 'layouts/auth-layout/DefaultAuthLayout';
import MainLayout from 'layouts/main-layout';
import Page404 from 'pages/errors/Page404';
import PageLoader from 'components/loading/PageLoader';
import paths, { rootPaths } from './paths';
import { lazyWithReload } from './lazyWithReload';

import AuthGuard from 'components/guard/AuthGuard';
import GuestGuard from 'components/guard/GuestGuard';
// import Splash from 'components/loading/Splash';

const LoggedOut = lazyWithReload(() => import('pages/authentication/default/LoggedOut'));

const Login = lazyWithReload(() => import('pages/authentication/default/jwt/Login'));
const Signup = lazyWithReload(() => import('pages/authentication/default/jwt/Signup'));
const ForgotPassword = lazyWithReload(
  () => import('pages/authentication/default/jwt/ForgotPassword'),
);
const TwoFA = lazyWithReload(() => import('pages/authentication/default/jwt/TwoFA'));
const VerifyEmail = lazyWithReload(() => import('pages/authentication/default/jwt/VerifyEmail'));
const SetPassword = lazyWithReload(() => import('pages/authentication/default/jwt/SetPassword'));
// Wedding App Pages
import CoupleLogin from 'pages/authentication/default/jwt/CoupleLogin';
import CreateEvent from 'pages/authentication/default/jwt/CreateEvent';
import CreateEventChooser from 'pages/authentication/default/jwt/CreateEventChooser';
const CoupleForgotPassword = lazyWithReload(
  () => import('pages/authentication/default/jwt/CoupleForgotPassword'),
);
const CoupleSetPassword = lazyWithReload(
  () => import('pages/authentication/default/jwt/CoupleSetPassword'),
);
// The couple's home page, "Your next step".
const CoupleDashboard = lazyWithReload(() => import('pages/couple/dashboard/CoupleDashboard'));
const CouplePlanner = lazyWithReload(() => import('pages/couple/CouplePlanner'));
const CoupleOrganizer = lazyWithReload(() => import('pages/couple/organizer/OrganizerPage'));
const CoupleOrganizerTasks = lazyWithReload(() => import('pages/couple/organizer/TasksBoardPage'));
const CoupleOrganizerBudget = lazyWithReload(() => import('pages/couple/organizer/BudgetPage'));
const CoupleOrganizerTimeline = lazyWithReload(() => import('pages/couple/organizer/TimelinePage'));
const CoupleOrganizerCategory = lazyWithReload(
  () => import('pages/couple/organizer/CategoryWorkspacePage'),
);
const CoupleOrganizerVendor = lazyWithReload(
  () => import('pages/couple/organizer/VendorDetailPage'),
);
// V1 (legacy, kept for rollback): import('pages/couple/CoupleInsights')
const CoupleInsights = lazyWithReload(() => import('pages/couple/CoupleInsightsV2'));
const CoupleQrCode = lazyWithReload(() => import('pages/couple/qr-code/CoupleQrCode'));
const CoupleQrCardDesign = lazyWithReload(() => import('pages/couple/qr-code/CoupleQrCardDesign'));
const CoupleInvitation = lazyWithReload(() => import('pages/couple/invitation/CoupleInvitation'));
const CoupleBilling = lazyWithReload(() => import('pages/couple/CoupleBilling'));
const CoupleUpload = lazyWithReload(() => import('pages/couple/CoupleUpload'));
const CoupleSettings = lazyWithReload(() => import('pages/couple/settings/CoupleSettings'));
const CoupleGuests = lazyWithReload(() => import('pages/couple/guests/GuestListPage'));
const CoupleSeating = lazyWithReload(() => import('pages/couple/seating/SeatingPage'));
const CoupleWishes = lazyWithReload(() => import('pages/couple/CoupleWishes'));
const CoupleQuiz = lazyWithReload(() => import('pages/couple/CoupleQuiz'));
const CoupleQuizResults = lazyWithReload(() => import('pages/couple/CoupleQuizResults'));
const GuestJoin = lazyWithReload(() => import('pages/wedding/GuestJoin'));
// V1 (legacy, kept for rollback): import('pages/wedding/GuestDashboard')
const GuestDashboard = lazyWithReload(() => import('pages/wedding/GuestHubV2'));
const GuestGallery = lazyWithReload(() => import('pages/wedding/GuestGallery'));
const GuestUpload = lazyWithReload(() => import('pages/wedding/GuestUpload'));
const GuestWishes = lazyWithReload(() => import('pages/wedding/GuestWishes'));
const GuestQuiz = lazyWithReload(() => import('pages/wedding/GuestQuiz'));
const GuestQuizResults = lazyWithReload(() => import('pages/wedding/GuestQuizResults'));
const GuestSeat = lazyWithReload(() => import('pages/wedding/GuestSeat'));
// Public interactive demo entry (unguarded) — mints a sandbox and redirects
// into the Heirloom dashboard. Linked from the marketing landing page.
const TryDemo = lazyWithReload(() => import('pages/sandbox/TryDemo'));

export const SuspenseOutlet = () => {
  const location = useLocation();

  return (
    <Suspense key={location.pathname} fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  );
};

/**
 * Short join URL redirect.
 * Accepts both the new path-segment shape (`/j/:eventId/c/:code`) and the
 * legacy query-string shape (`/j/:eventId?code=...`) for back-compat with
 * any links already sent. The code is forwarded to `/wedding/:eventId/join`
 * via router `state`, so it is consumed by GuestJoin without ever appearing
 * in the destination URL — no address-bar leak after the redirect, no
 * Referer leak on subsequent navigation, no analytics capture. See F-AUTH-09.
 */
const ShortJoinRedirect = () => {
  const { eventId, code: pathCode } = useParams<{
    eventId: string;
    code?: string;
  }>();
  const [searchParams] = useSearchParams();
  const queryCode = searchParams.get('code');
  const code = pathCode || queryCode || undefined;
  return (
    <Navigate
      to={`/wedding/${eventId}/join`}
      state={code ? { joinCode: code } : undefined}
      replace
    />
  );
};

export const routes: RouteObject[] = [
  {
    element: (
      // Uncomment the following line to enable the Suspense fallback for initial loading when using AuthGuard

      // <Suspense fallback={<Splash />}>
      <App />
      // </Suspense>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/couple/login" replace />,
      },

      // Public interactive demo — no guard (serves anonymous AND logged-in).
      // Mints a disposable Heirloom sandbox and redirects into the dashboard.
      // Reached from the marketing landing page ("Try the full plan").
      {
        path: '/try',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TryDemo />
          </Suspense>
        ),
      },

      {
        path: rootPaths.authRoot,
        element: (
          // Uncomment the following line to activate the GuestGurad for guest routes

          // <GuestGurad>
          <AuthLayout />
          // </GuestGurad>
        ),
        children: [
          {
            element: (
              <DefaultAuthLayout>
                <SuspenseOutlet />
              </DefaultAuthLayout>
            ),
            children: [
              {
                path: rootPaths.authDefaultJwtRoot,
                children: [
                  {
                    path: paths.defaultJwtLogin,
                    element: <Login />,
                  },
                  {
                    path: paths.defaultJwtSignup,
                    element: <Signup />,
                  },
                  {
                    path: paths.defaultJwtForgotPassword,
                    element: <ForgotPassword />,
                  },
                  {
                    path: paths.defaultJwt2FA,
                    element: <TwoFA />,
                  },
                  {
                    path: paths.defaultJwtSetPassword,
                    element: <SetPassword />,
                  },
                ],
              },
              {
                path: paths.defaultLoggedOut,
                element: <LoggedOut />,
              },
            ],
          },
        ],
      },

      // Wedding App Routes
      {
        path: '/couple',
        children: [
          // Auth routes (login, etc.) - use GuestGuard
          {
            element: (
              <GuestGuard>
                <AuthLayout />
              </GuestGuard>
            ),
            children: [
              {
                element: (
                  <DefaultAuthLayout>
                    <SuspenseOutlet />
                  </DefaultAuthLayout>
                ),
                children: [
                  {
                    path: 'login',
                    element: <CoupleLogin />,
                  },
                  // Event-type chooser — the entry point for new organizers
                  // (linked from the couple login). Routes on to one of the
                  // create-* flows below, forwarding `?plan=`.
                  {
                    path: 'start',
                    element: <CreateEventChooser />,
                    handle: { authImage: '/assets/agapae-hero.jpg' },
                  },
                  // Create-event is split per event type so each flow fixes its
                  // own `eventType` (no in-form toggle) and can show a different
                  // left-side hero via the route `handle.authImage` (read by
                  // DefaultAuthLayout). Wedding keeps the original URL so existing
                  // landing-page links and `?plan=` are unaffected.
                  {
                    path: 'create-event',
                    element: <CreateEvent eventType="wedding" />,
                    handle: { authImage: '/assets/agapae-hero.jpg' },
                  },
                  {
                    path: 'create-baptism',
                    element: <CreateEvent eventType="baptism" />,
                    // TODO: swap to a dedicated baptism hero when the asset exists.
                    handle: { authImage: '/assets/agapae-hero.jpg' },
                  },
                  {
                    path: 'create-wedding-baptism',
                    element: <CreateEvent eventType="wedding_baptism" />,
                    // TODO: swap to a dedicated combined hero when the asset exists.
                    handle: { authImage: '/assets/agapae-hero.jpg' },
                  },
                  {
                    path: 'forgot-password',
                    element: <CoupleForgotPassword />,
                  },
                  {
                    path: 'set-password',
                    element: <CoupleSetPassword />,
                  },
                ],
              },
            ],
          },
          // Email verification (requires auth, but not verification — uses auth layout)
          {
            element: (
              <AuthGuard skipVerification>
                <AuthLayout />
              </AuthGuard>
            ),
            children: [
              {
                element: (
                  <DefaultAuthLayout>
                    <SuspenseOutlet />
                  </DefaultAuthLayout>
                ),
                children: [
                  {
                    path: 'verify-email',
                    element: <VerifyEmail />,
                  },
                ],
              },
            ],
          },
          // Protected routes (dashboard, etc.) - use AuthGuard
          {
            element: (
              <AuthGuard>
                <MainLayout>
                  <SuspenseOutlet />
                </MainLayout>
              </AuthGuard>
            ),
            children: [
              {
                path: 'dashboard',
                element: <CoupleDashboard />,
              },
              {
                path: 'organizer',
                element: <CoupleOrganizer />,
              },
              {
                path: 'organizer/tasks',
                element: <CoupleOrganizerTasks />,
              },
              {
                path: 'organizer/budget',
                element: <CoupleOrganizerBudget />,
              },
              {
                path: 'organizer/timeline',
                element: <CoupleOrganizerTimeline />,
              },
              {
                path: 'organizer/categories/:slug',
                element: <CoupleOrganizerCategory />,
              },
              {
                path: 'organizer/vendors/:id',
                element: <CoupleOrganizerVendor />,
              },
              {
                path: 'insights',
                element: <CoupleInsights />,
              },
              {
                path: 'qr-code',
                element: <CoupleQrCode />,
              },
              {
                path: 'qr-code/design',
                element: <CoupleQrCardDesign />,
              },
              {
                path: 'invitation',
                element: <CoupleInvitation />,
              },
              {
                path: 'upload',
                element: <CoupleUpload />,
              },
              {
                path: 'billing',
                element: <CoupleBilling />,
              },
              {
                path: 'settings',
                element: <CoupleSettings />,
              },
              {
                path: 'guests',
                element: <CoupleGuests />,
              },
              {
                path: 'seating',
                element: <CoupleSeating />,
              },
              {
                path: 'wishes',
                element: <CoupleWishes />,
              },
              {
                path: 'quiz',
                element: <CoupleQuiz />,
              },
              {
                path: 'quiz/results',
                element: <CoupleQuizResults />,
              },
            ],
          },
          // Full-screen AI planner — couple-gated + email-verified (AuthGuard),
          // but full-bleed: rendered WITHOUT MainLayout so it has no sidenav /
          // app bar (Gemini-style focus page). The page itself is desktop-only.
          {
            element: (
              <AuthGuard>
                <SuspenseOutlet />
              </AuthGuard>
            ),
            children: [
              {
                path: 'planner',
                element: <CouplePlanner />,
              },
            ],
          },
        ],
      },

      {
        path: '/wedding',
        children: [
          // Guest Join routes - use GuestGuard
          {
            element: (
              <GuestGuard>
                <AuthLayout />
              </GuestGuard>
            ),
            children: [
              {
                element: (
                  <DefaultAuthLayout>
                    <SuspenseOutlet />
                  </DefaultAuthLayout>
                ),
                children: [
                  {
                    path: ':eventId/join',
                    element: <GuestJoin />,
                  },
                ],
              },
            ],
          },
          // Guest Protected routes - use AuthGuard
          {
            element: (
              <AuthGuard>
                <MainLayout>
                  <SuspenseOutlet />
                </MainLayout>
              </AuthGuard>
            ),
            children: [
              {
                path: ':eventId/dashboard',
                element: <GuestDashboard />,
              },
              {
                path: ':eventId/gallery',
                element: <GuestGallery />,
              },
              {
                path: ':eventId/album/:albumId',
                element: <GuestGallery />,
              },
              {
                path: ':eventId/upload',
                element: <GuestUpload />,
              },
              {
                path: ':eventId/wishes',
                element: <GuestWishes />,
              },
              {
                path: ':eventId/quiz',
                element: <GuestQuiz />,
              },
              {
                path: ':eventId/quiz/results',
                element: <GuestQuizResults />,
              },
              {
                path: ':eventId/seat',
                element: <GuestSeat />,
              },
            ],
          },
        ],
      },

      // Short join URL: /j/:eventId or /j/:eventId/c/:code → redirects to
      // /wedding/:eventId/join. The path-segment form keeps the join code
      // out of Referer headers and analytics query-string capture (F-AUTH-09).
      {
        path: '/j/:eventId',
        element: <ShortJoinRedirect />,
      },
      {
        path: '/j/:eventId/c/:code',
        element: <ShortJoinRedirect />,
      },

      {
        path: '*',
        element: <Page404 />,
      },
    ],
  },
];

const router = createBrowserRouter(routes, {
  basename: import.meta.env.VITE_BASENAME || '/',
});

export default router;
